import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ChevronDown, ChevronUp, Plus, Trash2, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Descriptive labels for puzzle pieces
const PIECE_LABELS = {
  'corner-1': { badge: 'C1', desc: '2Thess 2:1-3', longDesc: 'Corner 1 — 2 Thessalonians 2:1-3 (Troubled/Deceived)', lesson: 'Lesson 4' },
  'corner-2': { badge: 'C2', desc: 'Matt 24:29-31', longDesc: 'Corner 2 — Matthew 24:29-31 (Olivet Discourse)', lesson: 'Lesson 5' },
  'corner-3': { badge: 'C3', desc: '1Cor 15:51-52', longDesc: 'Corner 3 — 1 Corinthians 15:51-52 (Last Trumpet)', lesson: 'Lesson 6' },
  'corner-4': { badge: 'C4', desc: 'Rev 20:4-6', longDesc: 'Corner 4 — Revelation 20:4-6 (First Resurrection)', lesson: 'Lesson 7' },
  'border-1': { badge: 'B1', desc: 'Rev 11:15 (7th Trumpet)', longDesc: 'Border 1 — Revelation 11:15,18 (Seventh Trumpet = Last Trump)', lesson: 'Lesson 8' },
  'border-2': { badge: 'B2', desc: '1Thess 4:17 (Caught Up)', longDesc: 'Border 2 — 1 Thessalonians 4:17 (Caught Up — Apantesis)', lesson: 'Lesson 9' },
  'border-3': { badge: 'B3', desc: '2Thess 1:7-8 (Rest)', longDesc: 'Border 3 — 2 Thessalonians 1:7-8 (Rest When Revealed)', lesson: 'Lesson 10' },
  'border-4': { badge: 'B4', desc: '2Thess 2:3,8 (Destroyed)', longDesc: 'Border 4 — 2 Thessalonians 2:3,8 (Destroyed at His Coming)', lesson: 'Lesson 11' },
  'border-5': { badge: 'B5', desc: 'John 5:28-29 (Resurrections)', longDesc: 'Border 5 — John 5:28-29 (One Hour, Two Resurrections)', lesson: 'Lesson 12' },
  'border-6': { badge: 'B6', desc: 'Rev 16:15 (Keystone)', longDesc: 'Border 6 — Revelation 16:15 (I Come as a Thief — The Keystone)', lesson: 'Lesson 13' },
  'border-7': { badge: 'L3', desc: 'Recapitulation', longDesc: 'Legend 3 — Recapitulation (How Revelation Is Written)', lesson: 'Lesson 3' },
  'border-8': { badge: 'L2', desc: 'Blueprints (Mo\'edim)', longDesc: 'Legend 2 — The Blueprints (God\'s Divine Rehearsals)', lesson: 'Lesson 2' },
};

const AdminPuzzleEditor = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [crossRefs, setCrossRefs] = useState([]);
  const [publishedBooks, setPublishedBooks] = useState([]);
  const [generatingAI, setGeneratingAI] = useState(null); // ref_id being generated

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchPieces();
    fetchCrossRefs();
    fetchBooks();
  }, []);

  const getHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const fetchPieces = async () => {
    try {
      const res = await fetch(`${API_URL}/api/puzzle/admin/pieces`, {
        headers: getHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPieces(data.pieces || []);
      } else if (res.status === 403) {
        navigate('/');
      }
    } catch (e) { console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchCrossRefs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const allRefs = (data.cross_references || []).flatMap(ch => ch.connections || []);
        setCrossRefs(allRefs);
      }
    } catch (e) { console.error('Fetch cross-refs error:', e); }
  };

  const fetchBooks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/books/published`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPublishedBooks(data.books || []);
      }
    } catch (e) { console.error('Fetch books error:', e); }
  };

  const updateCrossRefLink = async (refId, bookId) => {
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references/${refId}/link`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ target_book_id: bookId, target_chapter_order: 1 }),
      });
      if (res.ok) {
        setSaveMsg('Link updated');
        fetchCrossRefs();
        setTimeout(() => setSaveMsg(''), 2000);
      }
    } catch (e) { console.error('Update link error:', e); }
  };

  const updateCrossRefDescription = async (refId, description) => {
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references/${refId}/description`, {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ description }),
      });
      if (res.ok) {
        setSaveMsg('Tagline saved');
        setTimeout(() => setSaveMsg(''), 2000);
      }
    } catch (e) { console.error('Update description error:', e); }
  };

  const addNewCrossRef = async (keyword, appendixCode, bookId) => {
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          keyword,
          appendix_code: appendixCode,
          target_book_id: bookId,
          description: '',
          target_chapter_order: 1,
        }),
      });
      if (res.ok) {
        toast.success('New appendix link added');
        fetchCrossRefs();
      }
    } catch (e) { console.error('Add cross-ref error:', e); }
  };

  const deleteCrossRef = async (refId) => {
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references/${refId}`, {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Link removed');
        fetchCrossRefs();
      }
    } catch (e) { console.error('Delete cross-ref error:', e); }
  };

  const generateAISummary = async (refId, bookId) => {
    setGeneratingAI(refId);
    try {
      const res = await fetch(`${API_URL}/api/mindmap/cross-references/generate-summary`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ ref_id: refId, book_id: bookId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('AI summary generated');
        // Update local state
        setCrossRefs(prev => prev.map(r =>
          r.ref_id === refId ? { ...r, description: data.summary } : r
        ));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'AI generation failed');
      }
    } catch (e) {
      console.error('Generate summary error:', e);
      toast.error('Failed to generate summary');
    }
    setGeneratingAI(null);
  };

  const handleFieldChange = (pieceId, field, value) => {
    setPieces(prev => prev.map(p => p.piece_id === pieceId ? { ...p, [field]: value } : p));
  };

  const handleLocalDescChange = (refId, value) => {
    setCrossRefs(prev => prev.map(r => r.ref_id === refId ? { ...r, description: value } : r));
  };

  const savePiece = async (piece) => {
    setSaving(piece.piece_id);
    try {
      const res = await fetch(`${API_URL}/api/puzzle/admin/pieces/${piece.piece_id}`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({
          title: piece.title, keyword: piece.keyword, anchor: piece.anchor,
          summary: piece.summary, git_verdict: piece.git_verdict, scripture_ref: piece.scripture_ref,
          scripture_text: piece.scripture_text, book_section_text: piece.book_section_text,
          git_verdict_text: piece.git_verdict_text,
        }),
      });
      if (res.ok) {
        setSaveMsg('Saved');
        setTimeout(() => setSaveMsg(''), 2000);
      }
    } catch (e) { console.error('Save error:', e); }
    setSaving(null);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0804' }}>
      <p className="text-amber-500/60 font-serif text-lg animate-pulse">Loading puzzle pieces...</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0a0804', color: '#fef3c7' }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-amber-600/60 hover:text-amber-500 text-sm font-serif">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </button>
          {saveMsg && <span className="text-[11px] font-mono tracking-wider text-emerald-500 animate-pulse">{saveMsg}</span>}
        </div>

        <h1 className="text-2xl font-serif mb-6 text-amber-200/90" style={{ letterSpacing: '0.05em' }}>
          Puzzle Piece Editor
        </h1>

        <div className="space-y-3">
          {pieces.sort((a, b) => (a.order || 0) - (b.order || 0)).map((piece) => {
            const isExpanded = expandedId === piece.piece_id;
            const isSaving = saving === piece.piece_id;
            const pieceRefs = crossRefs.filter(r =>
              r.keyword?.toLowerCase() === piece.keyword?.toLowerCase() ||
              r.keyword?.toLowerCase() === piece.anchor?.toLowerCase()
            );

            return (
              <div key={piece.piece_id} className="rounded-xl overflow-hidden border border-amber-900/15"
                style={{ background: 'linear-gradient(180deg, rgba(10,8,4,0.95), rgba(10,8,4,0.8))' }}
                data-testid={`puzzle-piece-${piece.piece_id}`}>

                {/* Header row */}
                <button onClick={() => setExpandedId(isExpanded ? null : piece.piece_id)}
                  className="w-full flex items-center justify-between px-5 py-3 text-left transition-all duration-300 hover:bg-amber-900/5"
                  data-testid={`expand-piece-${piece.piece_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-[10px] font-mono font-bold flex-shrink-0 px-2 py-0.5 rounded ${
                      piece.piece_id.startsWith('border-7') || piece.piece_id.startsWith('border-8')
                        ? 'bg-indigo-900/30 text-indigo-400'
                        : piece.piece_type === 'corner' ? 'bg-amber-900/30 text-amber-400' : 'bg-slate-900/30 text-slate-200'
                    }`}>
                      {PIECE_LABELS[piece.piece_id]?.badge || `#${piece.order}`}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-serif text-amber-200/80 truncate">
                        {PIECE_LABELS[piece.piece_id]?.longDesc || piece.title}
                      </p>
                      {PIECE_LABELS[piece.piece_id]?.lesson && (
                        <p className="text-[9px] text-amber-600/50 font-mono">{PIECE_LABELS[piece.piece_id].lesson}</p>
                      )}
                    </div>
                    <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-amber-900/15 text-amber-600/60 flex-shrink-0">{piece.keyword}</span>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-amber-700/40" /> : <ChevronDown className="w-4 h-4 text-amber-700/40" />}
                </button>

                {/* Expanded editor */}
                {isExpanded && (
                  <div className="px-5 pb-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Title" value={piece.title || ''} onChange={v => handleFieldChange(piece.piece_id, 'title', v)} testId={`field-title-${piece.piece_id}`} />
                      <Field label="Keyword" value={piece.keyword || ''} onChange={v => handleFieldChange(piece.piece_id, 'keyword', v)} testId={`field-keyword-${piece.piece_id}`} />
                    </div>
                    <Field label="Anchor (Tagline)" value={piece.anchor || ''} onChange={v => handleFieldChange(piece.piece_id, 'anchor', v)} testId={`field-anchor-${piece.piece_id}`} />
                    <FieldArea label="Summary" value={piece.summary || ''} rows={3}
                      onChange={v => handleFieldChange(piece.piece_id, 'summary', v)} testId={`field-summary-${piece.piece_id}`} />
                    <Field label="Scripture Reference" value={piece.scripture_ref || ''} onChange={v => handleFieldChange(piece.piece_id, 'scripture_ref', v)} testId={`field-scripture-${piece.piece_id}`} />

                    <div className="pt-3 border-t border-amber-900/15">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-amber-600/50 mb-2 font-serif">Scripture Text (the actual Bible passage displayed on the scroll)</p>
                      <FieldArea label="" value={piece.scripture_text || ''} rows={5}
                        placeholder="The full Bible passage text (KJV or your preferred translation). This is what the reader sees first on each scroll."
                        onChange={v => handleFieldChange(piece.piece_id, 'scripture_text', v)} testId={`field-scripture-${piece.piece_id}`} />
                    </div>

                    <FieldArea label="GIT Verdict Description" value={piece.git_verdict_text || ''} rows={2}
                      onChange={v => handleFieldChange(piece.piece_id, 'git_verdict_text', v)} testId={`field-verdict-text-${piece.piece_id}`} />

                    <div className="pt-3 border-t border-amber-900/15">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-amber-600/50 mb-2 font-serif">Book Section Text (displayed on the ancient scroll)</p>
                      <FieldArea label="" value={piece.book_section_text || ''} rows={6}
                        placeholder="Paste the concise text from your book about this puzzle piece. This will display on the ancient scroll image in the 'For Deeper Study' section."
                        onChange={v => handleFieldChange(piece.piece_id, 'book_section_text', v)} testId={`field-book-text-${piece.piece_id}`} />
                    </div>

                    {/* Appendix Cross-Reference Links */}
                    <div className="pt-3 border-t border-amber-900/15" data-testid={`appendix-links-${piece.piece_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-amber-600/50 font-serif">
                          Appendix Links ({pieceRefs.length} connections)
                        </p>
                        <AddLinkButton
                          keyword={piece.keyword}
                          publishedBooks={publishedBooks}
                          onAdd={(code, bookId) => addNewCrossRef(piece.keyword, code, bookId)}
                        />
                      </div>
                      <div className="space-y-3">
                        {pieceRefs.map((ref, ri) => {
                          const linkedBook = publishedBooks.find(b => b.book_id === ref.target_book_id);
                          const isGenerating = generatingAI === ref.ref_id;
                          return (
                            <div key={ref.ref_id || ri} className="rounded-lg border border-amber-900/10 p-3"
                              style={{ background: 'rgba(10,8,4,0.5)' }}>
                              {/* Row 1: Code + Book selector + actions */}
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-amber-900/20 text-amber-500 flex-shrink-0">
                                  {ref.appendix_code}
                                </span>
                                <select
                                  value={ref.target_book_id || ''}
                                  onChange={e => updateCrossRefLink(ref.ref_id, e.target.value)}
                                  data-testid={`select-link-${ref.appendix_code}`}
                                  className="flex-1 text-[10px] px-2 py-1 rounded border border-amber-900/20 bg-[#0a0804] text-amber-300 font-mono"
                                >
                                  {publishedBooks.map(b => (
                                    <option key={b.book_id} value={b.book_id}>{b.title?.substring(0, 50)}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => generateAISummary(ref.ref_id, ref.target_book_id)}
                                  disabled={isGenerating}
                                  title="Generate AI summary"
                                  data-testid={`ai-summary-btn-${ref.ref_id}`}
                                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono bg-purple-900/30 text-purple-300 hover:bg-purple-800/40 border border-purple-700/30 disabled:opacity-50 transition-colors flex-shrink-0"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  {isGenerating ? 'AI...' : 'AI'}
                                </button>
                                <button
                                  onClick={() => deleteCrossRef(ref.ref_id)}
                                  title="Remove link"
                                  data-testid={`delete-link-${ref.ref_id}`}
                                  className="p-1 rounded text-red-500/50 hover:text-red-400 hover:bg-red-900/20 transition-colors flex-shrink-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                              {/* Row 2: Editable description/tagline */}
                              <div className="flex items-start gap-2">
                                <input
                                  type="text"
                                  value={ref.description || ''}
                                  onChange={e => handleLocalDescChange(ref.ref_id, e.target.value)}
                                  onBlur={() => updateCrossRefDescription(ref.ref_id, ref.description || '')}
                                  placeholder="Type your tagline for this link..."
                                  data-testid={`desc-input-${ref.ref_id}`}
                                  className="flex-1 px-2 py-1.5 rounded bg-[#0a0805] border border-amber-900/20 text-amber-200 text-xs font-serif focus:border-amber-700/50 focus:outline-none transition-colors placeholder:text-amber-900/30"
                                />
                              </div>
                              <p className="text-[9px] text-amber-700/50 mt-1">
                                {linkedBook?.title || 'Unknown book'} (ch#{ref.target_chapter_order || '?'})
                              </p>
                            </div>
                          );
                        })}
                        {pieceRefs.length === 0 && (
                          <p className="text-xs text-amber-600/60 italic">No appendix links yet. Click + Add to create one.</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button onClick={() => savePiece(piece)} disabled={isSaving}
                        data-testid={`btn-save-${piece.piece_id}`}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-serif transition-all duration-300 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #92400e, #b45309)', color: '#fef3c7' }}>
                        <Save className="w-4 h-4" />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Add Link inline UI
function AddLinkButton({ keyword, publishedBooks, onAdd }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [bookId, setBookId] = useState('');

  const appendixBooks = publishedBooks.filter(b =>
    b.title?.toLowerCase().includes('appendix')
  );

  const handleAdd = () => {
    if (!code.trim() || !bookId) return;
    onAdd(code.trim().toUpperCase(), bookId);
    setCode('');
    setBookId('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="add-link-btn"
        className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono bg-emerald-900/30 text-emerald-400 hover:bg-emerald-800/40 border border-emerald-700/30 transition-colors"
      >
        <Plus className="w-3 h-3" /> Add Link
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 bg-[#0a0805] border border-amber-900/20 rounded-lg px-2 py-1">
      <input
        type="text"
        value={code}
        onChange={e => setCode(e.target.value)}
        placeholder="Code (e.g. AL)"
        data-testid="add-link-code"
        className="w-16 px-1.5 py-1 rounded bg-transparent border border-amber-900/20 text-amber-200 text-[10px] font-mono focus:outline-none"
      />
      <select
        value={bookId}
        onChange={e => setBookId(e.target.value)}
        data-testid="add-link-book"
        className="text-[10px] px-1.5 py-1 rounded border border-amber-900/20 bg-transparent text-amber-300 font-mono max-w-[180px]"
      >
        <option value="">Select book...</option>
        {(appendixBooks.length > 0 ? appendixBooks : publishedBooks).map(b => (
          <option key={b.book_id} value={b.book_id}>{b.title?.substring(0, 40)}</option>
        ))}
      </select>
      <button onClick={handleAdd} disabled={!code.trim() || !bookId}
        className="px-2 py-1 rounded text-[9px] font-mono bg-emerald-700 text-white disabled:opacity-30 hover:bg-emerald-600">
        Add
      </button>
      <button onClick={() => setOpen(false)}
        className="px-1 py-1 rounded text-[9px] text-amber-700 hover:text-amber-500">
        Cancel
      </button>
    </div>
  );
}

// Reusable field components
function Field({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      {label && <label className="text-[10px] tracking-wider uppercase text-amber-600/70 mb-1 block font-mono">{label}</label>}
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-lg bg-[#0a0805] border border-amber-900/20 text-amber-200 text-sm font-serif focus:border-amber-700/50 focus:outline-none transition-colors placeholder:text-amber-900/30" />
    </div>
  );
}

function FieldArea({ label, value, onChange, testId, rows = 3, placeholder }) {
  return (
    <div>
      {label && <label className="text-[10px] tracking-wider uppercase text-amber-600/70 mb-1 block font-mono">{label}</label>}
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-lg bg-[#0a0805] border border-amber-900/20 text-amber-200 text-sm font-serif leading-relaxed focus:border-amber-700/50 focus:outline-none transition-colors resize-y placeholder:text-amber-900/30" />
    </div>
  );
}

export default AdminPuzzleEditor;
